import React, { useEffect, useState } from 'react';
import {
  Button,
  Checkbox,
  Dimmer,
  Dropdown,
  Form,
  Grid,
  Icon,
  Input,
  Loader,
  Message,
  Modal,
  Tab,
  TextArea
} from 'semantic-ui-react';

import { FieldLabel } from '../../../../styles';
import { get } from '../../../../services/request';
import { maskMoney, unmaskMoney, numberToMoneyString } from '../../../../services/masks';
import { updateConsultativeQuote } from '../../../../services/fire-insurance-quotation';
import {
  dangerNotification,
  successNotification,
  warningNotification
} from '../../../../services/notification';
import { MoneyInputWrapper, CoverageRow } from '../../policy-list/AdminEditPolicyModal/styles';
import { translateStatus } from '../../utils/Pottencial';

const STATUS_UNDER_ANALYSIS = 'UNDERANALYSIS';
const STATUS_APPROVED = 'APPROVED';
const STATUS_DENIED = 'DENIED';

type CoverageForm = {
  id?: string;
  coverageName: string;
  referenceCode?: string;
  value: string;
  totalPremium: string;
  enabled: boolean;
};

type ConsultativeAdminModalProps = {
  open: boolean;
  /** Item da listagem de orçamentos (analysis com fireInsurance/estate). */
  item: any;
  onClose: () => void;
  onSaved: () => void;
};

const moneyToForm = (value?: number | string | null): string => {
  if (value === null || value === undefined || value === '') {
    return '';
  }
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) {
    return '';
  }
  return maskMoney(parsed.toFixed(2), true);
};

const parseMoney = (value: string): number | null => {
  if (!value || !String(value).trim()) {
    return null;
  }
  const parsed = unmaskMoney(String(value));
  return Number.isFinite(parsed) ? parsed : null;
};

const maskedInput = (raw: string): string => {
  const digits = String(raw).replace(/\D/g, '');
  if (!digits) {
    return '';
  }
  return maskMoney((Number(digits) / 100).toFixed(2), true);
};

const errorMessage = (error: any): string => {
  const message = error?.response?.data?.message ?? error?.message;
  if (Array.isArray(message)) {
    return message.join(' ');
  }
  return message || 'Erro inesperado. Tente novamente.';
};

const ConsultativeAdminModal = ({
  open,
  item,
  onClose,
  onSaved
}: ConsultativeAdminModalProps) => {
  const [loading, setLoading] = useState<boolean>(true);
  const [saving, setSaving] = useState<boolean>(false);
  const [analysis, setAnalysis] = useState<any>(null);

  const [status, setStatus] = useState<string>(item.status);
  const [clientInstallments, setClientInstallments] = useState<number>(12);
  const [installmentValue, setInstallmentValue] = useState<string>('');
  const [coverages, setCoverages] = useState<CoverageForm[]>([]);
  const [newCoverageName, setNewCoverageName] = useState<string>('');
  const [newCoverageValue, setNewCoverageValue] = useState<string>('');


  const fireInsurance = analysis?.fireInsurance || item.fireInsurance;

  useEffect(() => {
    let active = true;

    const load = async () => {
      setLoading(true);
      try {
        const data = await get(`/fire-insurance-quotations/budget/${item.id}`);
        if (!active) return;

        setAnalysis(data);
        setStatus(data.status);
        setClientInstallments(Number(data.clientInstallments) || 12);
        setInstallmentValue(moneyToForm(data.clientInstallmentValue));
        setCoverages(
          (data.coverages || []).map((coverage: any) => ({
            id: coverage.id,
            coverageName: coverage.coverageName || coverage.referenceCode || '',
            referenceCode: coverage.referenceCode,
            value: moneyToForm(coverage.value),
            totalPremium: moneyToForm(coverage.totalPremium),
            enabled: coverage.enabled !== false
          }))
        );
      } catch (error) {
        dangerNotification('Erro!', 'Não foi possível carregar o orçamento consultivo.');
        onClose();
      } finally {
        if (active) setLoading(false);
      }
    };

    load();

    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item.id]);

  const statusOptions = [
    { key: STATUS_UNDER_ANALYSIS, value: STATUS_UNDER_ANALYSIS, text: 'Em análise' },
    { key: STATUS_APPROVED, value: STATUS_APPROVED, text: 'Aprovado (precificado)' },
    { key: STATUS_DENIED, value: STATUS_DENIED, text: 'Recusado' }
  ];

  const updateCoverage = (index: number, patch: Partial<CoverageForm>) =>
    setCoverages(prev =>
      prev.map((coverage, i) => (i === index ? { ...coverage, ...patch } : coverage))
    );

  const addCoverage = () => {
    if (!newCoverageName.trim() || !parseMoney(newCoverageValue)) {
      warningNotification(
        'Cobertura inválida',
        'Informe o nome e um valor maior que zero para a nova cobertura.'
      );
      return;
    }
    setCoverages(prev => [
      ...prev,
      {
        coverageName: newCoverageName.trim(),
        value: newCoverageValue,
        totalPremium: '',
        enabled: true
      }
    ]);
    setNewCoverageName('');
    setNewCoverageValue('');
  };

  const handleSave = async () => {
    // O backoffice informa as parcelas e o valor da parcela da seguradora, e eles
    // são gravados exatamente como informados (sem converter para 12x/mensal).
    const installmentNumber = parseMoney(installmentValue);
    const totalNumber = installmentNumber
      ? Number((installmentNumber * clientInstallments).toFixed(2))
      : null;

    if (status === STATUS_APPROVED && (!installmentNumber || installmentNumber <= 0)) {
      warningNotification(
        'Valor obrigatório',
        'Informe o valor da parcela na aba Valores para aprovar o orçamento.'
      );
      return;
    }

    setSaving(true);
    try {
      await updateConsultativeQuote(item.id, {
        status,
        analysis: {
          value: installmentNumber,
          grossPremium: totalNumber,
          installments: clientInstallments,
          clientInstallments,
          clientInstallmentValue: installmentNumber
        },
        coverages: coverages.map(coverage => ({
          id: coverage.id,
          coverageName: coverage.coverageName,
          referenceCode: coverage.referenceCode,
          value: parseMoney(coverage.value),
          totalPremium: parseMoney(coverage.totalPremium),
          enabled: coverage.enabled
        }))
      });

      successNotification('Sucesso!', 'Análise do orçamento consultivo salva.');
      onSaved();
      onClose();
    } catch (error) {
      dangerNotification('Erro ao salvar análise', errorMessage(error));
    } finally {
      setSaving(false);
    }
  };

  const renderMoney = (
    label: string,
    value: string,
    onChange: (value: string) => void,
    width: 4 | 8 | 16 = 8
  ) => (
    <Grid.Column width={width}>
      <FieldLabel>{label}</FieldLabel>
      <MoneyInputWrapper>
        <Input
          fluid
          disabled={saving}
          value={value}
          onChange={(_, { value: next }) => onChange(maskedInput(String(next)))}
        />
      </MoneyInputWrapper>
    </Grid.Column>
  );

  const requestPane = (
    <Tab.Pane attached={false}>
      <Grid>
        <Grid.Row>
          <Grid.Column width={8}>
            <FieldLabel>Imobiliária</FieldLabel>
            <Input
              fluid
              disabled
              value={fireInsurance?.estate?.name || item.fireInsurance?.estate?.name || ''}
            />
          </Grid.Column>
          <Grid.Column width={8}>
            <FieldLabel>Inquilino</FieldLabel>
            <Input fluid disabled value={fireInsurance?.tenantName || ''} />
          </Grid.Column>
        </Grid.Row>
        <Grid.Row>
          <Grid.Column width={8}>
            <FieldLabel>Tipo do imóvel</FieldLabel>
            <Input fluid disabled value={fireInsurance?.occupation || ''} />
          </Grid.Column>
          <Grid.Column width={8}>
            <FieldLabel>Estimativa convencional (referência, mensal)</FieldLabel>
            <Input
              fluid
              disabled
              value={
                analysis?.conventionalEstimateValue
                  ? numberToMoneyString(Number(analysis.conventionalEstimateValue))
                  : '-'
              }
            />
          </Grid.Column>
        </Grid.Row>
        <Grid.Row>
          <Grid.Column width={16}>
            <FieldLabel>Atividade exercida no imóvel</FieldLabel>
            <Form>
              <TextArea rows={3} disabled value={fireInsurance?.propertyActivity || ''} />
            </Form>
          </Grid.Column>
        </Grid.Row>
        <Grid.Row>
          <Grid.Column width={8}>
            <FieldLabel>Decisão</FieldLabel>
            <Dropdown
              fluid
              selection
              disabled={saving}
              options={statusOptions}
              value={status}
              onChange={(_, { value }) => setStatus(String(value))}
            />
          </Grid.Column>
        </Grid.Row>
      </Grid>
    </Tab.Pane>
  );

  const valuesPane = (
    <Tab.Pane attached={false}>
      <Message info size="small">
        Informe as parcelas e o valor da parcela retornados pela seguradora. Ao aprovar, o
        orçamento passa a ter valor definido e a imobiliária é avisada por e-mail.
      </Message>
      <Grid>
        <Grid.Row>
          <Grid.Column width={8}>
            <FieldLabel>Parcelas</FieldLabel>
            <Dropdown
              fluid
              selection
              disabled={saving}
              options={Array.from({ length: 12 }, (_, index) => ({
                key: index + 1,
                value: index + 1,
                text: `${index + 1}x`
              }))}
              value={clientInstallments}
              onChange={(_, { value }) => {
                const installments = Number(value);
                setClientInstallments(installments);
              }}
            />
          </Grid.Column>
          {renderMoney('Valor da parcela', installmentValue, setInstallmentValue)}
        </Grid.Row>
      </Grid>
    </Tab.Pane>
  );

  const coveragesPane = (
    <Tab.Pane attached={false}>
      {coverages.map((coverage, index) => (
        <CoverageRow key={coverage.id || `new-${index}`} $disabled={!coverage.enabled}>
          <Grid>
            <Grid.Row>
              <Grid.Column width={11} verticalAlign="middle">
                <FieldLabel>{coverage.coverageName || 'Cobertura'}</FieldLabel>
              </Grid.Column>
              <Grid.Column width={5} verticalAlign="middle">
                <Checkbox
                  label="Ativa"
                  checked={coverage.enabled}
                  disabled={saving}
                  onChange={(_, { checked }) => updateCoverage(index, { enabled: !!checked })}
                />
              </Grid.Column>
            </Grid.Row>
            <Grid.Row>
              {renderMoney(
                'Valor (LMI)',
                coverage.value,
                value => updateCoverage(index, { value }),
                8
              )}
              {renderMoney(
                'Prêmio',
                coverage.totalPremium,
                totalPremium => updateCoverage(index, { totalPremium }),
                8
              )}
            </Grid.Row>
          </Grid>
        </CoverageRow>
      ))}

      {coverages.length === 0 && (
        <Message info>Nenhuma cobertura cadastrada neste orçamento.</Message>
      )}

      <Grid>
        <Grid.Row verticalAlign="bottom">
          <Grid.Column width={8}>
            <FieldLabel>Nova cobertura / assistência</FieldLabel>
            <Input
              fluid
              disabled={saving}
              placeholder="Nome"
              value={newCoverageName}
              onChange={(_, { value }) => setNewCoverageName(String(value))}
            />
          </Grid.Column>
          <Grid.Column width={5}>
            <MoneyInputWrapper>
              <Input
                fluid
                disabled={saving}
                placeholder="Valor"
                value={newCoverageValue}
                onChange={(_, { value }) => setNewCoverageValue(maskedInput(String(value)))}
              />
            </MoneyInputWrapper>
          </Grid.Column>
          <Grid.Column width={3}>
            <Button fluid disabled={saving} onClick={addCoverage}>
              <Icon name="plus" /> Adicionar
            </Button>
          </Grid.Column>
        </Grid.Row>
      </Grid>
    </Tab.Pane>
  );

  const panes = [
    { menuItem: 'Solicitação e decisão', render: () => requestPane },
    { menuItem: 'Valores', render: () => valuesPane },
    { menuItem: 'Coberturas', render: () => coveragesPane }
  ];

  return (
    <Modal open={open} onClose={onClose} size="large" closeOnDimmerClick={false}>
      <Modal.Header>
        <Icon name="clipboard check" />
        Análise de orçamento consultivo
        <span style={{ fontSize: 14, fontWeight: 'normal', marginLeft: 12, color: '#777' }}>
          {translateStatus(item.status)}
        </span>
      </Modal.Header>

      <Modal.Content scrolling>
        <Dimmer active={loading || saving} inverted>
          <Loader />
        </Dimmer>
        {!loading && <Tab menu={{ secondary: true, pointing: true }} panes={panes} />}
      </Modal.Content>

      <Modal.Actions>
        <Button disabled={saving} onClick={onClose}>
          Fechar
        </Button>
        <Button positive disabled={saving || loading} onClick={handleSave}>
          <Icon name="save" /> Salvar análise
        </Button>
      </Modal.Actions>
    </Modal>
  );
};

export default ConsultativeAdminModal;
